import React from "react";
import axios, { AxiosPromise } from "axios";
import firebase from "firebase/app";
import "firebase/auth";
import moment from "moment";

import {
  AndroidIcon,
  AndroidOsIcon,
  CodeIcon,
  ComputerMouseIcon,
  EditPropertyIcon,
  FingerprintIcon,
  HeightIcon,
  HubspotIcon,
  InternetConnectionIcon,
  LoadBalancerIcon,
  NaturalUserInterface2Icon,
  SalesforceIcon,
  StripeIcon,
  TermIcon,
  ToggleOnIcon,
} from "./components/icons/themes/windows10";
import { API_ROOT } from "./config";
import { envLocker, LocalStorageEnvironment } from "./reducers/environments";

// Make a request to the perfalytics API endpoint with the given request.
export function makeRequest<T>(
  kind: string,
  request: Record<string, any> | undefined = {}
): AxiosPromise<T> {
  const localStorageEnv = envLocker.getCurrentEnv();
  const currentUser = firebase.auth().currentUser;

  if (currentUser) {
    return currentUser.getIdToken().then((token) => {
      const query: Record<string, any> = {
        kind: kind,
        token: token,
        request: request,
        envID: localStorageEnv ? localStorageEnv.ID : null,
        email: currentUser.email,
      };

      if (masqueradeToken()) {
        query.masquerade_token = masqueradeToken();
      }

      return axios.request<T>({
        method: "post",
        url: `${API_ROOT}/list?t=${kind}`,
        data: query,
      });
    });
  } else {
    return Promise.reject("User undefined, cannot make requests");
  }
}

export const MASQUERADE_LOCAL_STORAGE_KEY = "masquerade_token";

// Causes the current user to begin masquarading under the given
// environment or user. Masquarading lasts for one hour.
export function masqueradeAs(token: string) {
  const data = {
    expires: +moment().add(1, "hour"),
    token: token,
  };

  localStorage.setItem(MASQUERADE_LOCAL_STORAGE_KEY, JSON.stringify(data));
}

// Gets the current token the user is masquerading under.
export function masqueradeToken(): string | null {
  const data = localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY);
  if (data === null) {
    return null;
  }

  const { expires, token } = JSON.parse(data);
  if (expires === undefined || token === undefined) {
    return null;
  }

  if (moment().isAfter(moment(expires))) {
    stopMasquerading();
    return null;
  }

  return token;
}

// Causes the current user to stop masquerading.
export function stopMasquerading() {
  localStorage.removeItem(MASQUERADE_LOCAL_STORAGE_KEY);
}

// Returns the number of minutes the user has left masquerading.
export function masqueradingMinutesLeft(): number | null {
  const data = localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY);
  if (data === null) {
    return null;
  }

  const { expires, token } = JSON.parse(data);
  if (expires === undefined || token === undefined) {
    return null;
  }

  const now = moment();
  const expiresTime = moment(expires);
  if (now.isAfter(expiresTime)) {
    stopMasquerading();
    return null;
  }

  return moment.duration(expiresTime.diff(now)).minutes();
}

// Returns true if this user is allowed to masquerade.
export function userIsFPEmployee(): boolean {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    return false;
  }
  const uid = currentUser.uid;
  const freshpaintUIDs = [
    "AH5R2V9WwrPMUqNiuwFTjUEUWhC2", // Michael Malis
    "KavQITSjseTwbwiiz0VnSMGAJPZ2", // Steven Fitzsimmons
    "clGpSZACXCaDc5fHIirB1YtOx5Q2", // Andrew Webb
    "dxxa9mKXKpYYioNsFV2O8kczea63", // James Taber
    "WokFV9SqInggsB1DfXleXq0Bm0o1", // Kyle Pokorny
    "ieHKwYvyknaSZT9gbsZKsj7wjg43", // Ryan Orsini
  ];

  return freshpaintUIDs.includes(uid);
}

export const iconForEventType = (eventType: string): React.ReactNode => {
  switch (eventType) {
    case "pageview":
      return InternetConnectionIcon;
    case "click":
      return ComputerMouseIcon;
    case "submit":
      return TermIcon;
    case "change":
      return EditPropertyIcon;
    case "$identify":
      return FingerprintIcon;
    case "manual_tracked":
      return CodeIcon;
    case "server":
      return LoadBalancerIcon;
    case "analytics-android":
      return AndroidOsIcon;
    case "analytics-ios":
      return AndroidOsIcon;
    case "touch":
      return NaturalUserInterface2Icon;
    case "text_edit":
      return EditPropertyIcon;
    case "toggle":
      return ToggleOnIcon;
    case "react_navigation_screenview":
      return AndroidIcon;
    case "scroll_view_page":
      return HeightIcon;
    case "rn_custom":
      return CodeIcon;
    case "sendinblue":
      // :TODO: (jmtaber129): Change this to the Sendinblue icon once we have one.
      return CodeIcon;
    case "mailchimp":
      return CodeIcon;
    case "intercom":
      return CodeIcon;
    default:
      return CodeIcon;
  }
};

export const iconForEventSource = (eventSource: string): React.FC<any> => {
  switch (eventSource) {
    case "autotrack":
      return CodeIcon;
    case "hubspot":
      return HubspotIcon;
    case "stripe":
      return StripeIcon;
    case "sfdc":
      return SalesforceIcon;
    default:
      return () => null;
  }
};

export const eventSourceDisplay = (eventSource: string): string => {
  switch (eventSource) {
    case "autotrack":
      return "Web";
    case "hubspot":
      return "HubSpot";
    case "stripe":
      return "Stripe";
    case "sfdc":
      return "SaleForce";
    default:
      return "Custom";
  }
};

export const isValidUrl = (inputUrl: string): boolean => {
  try {
    new URL(inputUrl);
  } catch (_) {
    return false;
  }
  return true;
};
